import React, { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Button, { SecondaryButton } from '../../styled/components/Button';
import StyledTextArea from './StyledTextArea';
import { matchSliceActions, selectActiveMatchEvents, selectMatch } from '../../features/match/matchSlice';
import { inputSliceActions, selectActiveEventTypeId, selectComment } from '../../features/input/inputSlice';
import { MatchEventType } from '../../enums/matchEventType';
import { useSelector } from 'react-redux';
import { keyboardActions, selectActiveFocusItem } from '../../features/keyboard/keyboardSlice';
import { ActiveFocusItemName } from '../../features/keyboard/keyboardSliceTypes';
import lodash from 'lodash';
import getActiveMatchEvent from '../../features/input/helpers/getActiveMatchEvent';
import ReactTextareaAutocomplete from '@webscopeio/react-textarea-autocomplete';
import eventIcons from '../../constants/icons';
import { RefereeType } from '../../enums/refereeType';
import publishOrUpdateEvent from '../../features/input/thunks/publishOrUpdateEvent';
import { useAppDispatch } from '../../hooks/hooks';

type ItemProps = {
  shirtNumber: string;
  name: string;
  onTheField?: boolean;
  refereeTypeId?: number;
};

type EntityProps = {
  entity: ItemProps;
};

const Item = ({ entity: { name, shirtNumber, refereeTypeId, onTheField } }: EntityProps) => {
  if (refereeTypeId) {
    let icon: string | undefined;
    if (refereeTypeId === RefereeType.REFEREE) {
      icon = eventIcons[MatchEventType.PENALTY];
    } else if (refereeTypeId === RefereeType.ASSISTANT_REFEREE) {
      icon = eventIcons[MatchEventType.OFFSIDE];
    } else if (refereeTypeId === RefereeType.VAR_REFEREE) {
      icon = eventIcons[MatchEventType.VAR];
    }
    return (
      <div className="rta__item-item referee">
        <div className="rta__item-item-shirt-number">
          {icon && icon.length > 0 && <img className="icon" src={icon} alt="icon" />}
        </div>
        <div>{`${name}`}</div>
      </div>
    );
  } else {
    const className = onTheField === true ? 'onTheField' : onTheField === false ? 'otherMatchFacts' : '';
    return (
      <div className={className}>
        <div className="rta__item-item-shirt-number">{`${shirtNumber}`}</div>
        <div>{`${name}`}</div>
      </div>
    );
  }
};

const dataProviderName = (token, match) => {
  if (token.toLowerCase().startsWith('h')) {
    return match.homeTeam.persons
      .filter((person) => {
        const search = token.replace('h', '');
        if (search) {
          if (isNaN(parseInt(search))) {
            return person.name.toLowerCase().includes(search.toLowerCase());
          } else if (person.shirtNumber) {
            return person.shirtNumber.toString().startsWith(search);
          }
        }
        return true;
      })
      .map((person) => {
        const obj: ItemProps = {
          shirtNumber: person.shirtNumber ? person.shirtNumber.toString() : '',
          name: person.name ?? '',
          onTheField: person.currentlyOnTheField,
        };
        return obj;
      });
  } else if (token.toLowerCase().startsWith('a')) {
    return match.awayTeam.persons
      .filter((person) => {
        const search = token.replace('a', '');
        if (search) {
          if (isNaN(parseInt(search))) {
            return person.name.toLowerCase().includes(search.toLowerCase());
          } else if (person.shirtNumber) {
            return person.shirtNumber.toString().startsWith(search);
          }
        }
        return true;
      })
      .map((person) => {
        const obj: ItemProps = {
          shirtNumber: person.shirtNumber ? person.shirtNumber.toString() : '',
          name: person.name ?? '',
          onTheField: person.currentlyOnTheField,
        };
        return obj;
      });
  } else {
    return [];
  }
};

const dataProviderShirtNumber = (token, match) => {
  let personsH: ItemProps[] = [];
  if (!isNaN(parseInt(token)) || token.toLowerCase().startsWith('h')) {
    let searchToken = token;
    if (searchToken.toLowerCase().startsWith('h')) {
      searchToken = searchToken.substring(1);
    }
    personsH = match.homeTeam.persons
      .filter((person) => {
        if (person.shirtNumber) {
          return person.shirtNumber.toString().startsWith(searchToken);
        }
        return false;
      })
      .map((person) => {
        const obj: ItemProps = {
          shirtNumber: person.shirtNumber ? person.shirtNumber.toString() : '',
          name: person.name ?? '',
          onTheField: person.currentlyOnTheField,
        };
        return obj;
      });
  }

  let personsA: ItemProps[] = [];
  if (!token.toLowerCase().startsWith('h') && (!isNaN(parseInt(token)) || token.toLowerCase().startsWith('a'))) {
    if (token.toLowerCase().startsWith('a')) {
      token = token.substring(1);
    }
    personsA = match.awayTeam.persons
      .filter((person) => {
        if (person.shirtNumber) {
          return person.shirtNumber.toString().startsWith(token);
        }
        return false;
      })
      .map((person) => {
        const obj: ItemProps = {
          shirtNumber: person.shirtNumber ? person.shirtNumber.toString() : '',
          name: person.name ?? '',
          onTheField: person.currentlyOnTheField,
        };
        return obj;
      });
  }
  return personsH.concat(personsA);
};

const dataProviderOtherMatchFacts = (token, match) => {
  if (token.toLowerCase().startsWith('h')) {
    const obj: ItemProps = {
      shirtNumber: '',
      name: match.homeTeam.headCoach?.name ?? '',
    };
    return [obj];
  } else if (token.toLowerCase().startsWith('a')) {
    const obj: ItemProps = {
      shirtNumber: '',
      name: match.awayTeam.headCoach?.name ?? '',
    };
    return [obj];
  } else if (token.toLowerCase().startsWith('s')) {
    if (match.stadium) {
      const obj: ItemProps = {
        shirtNumber: '',
        name: match.stadium.name ?? '',
      };
      return [obj];
    }
  } else if (token.toLowerCase().startsWith('r')) {
    if (match.referees) {
      return match.referees.map((person) => {
        const obj: ItemProps = {
          shirtNumber: '',
          name: person.name ?? '',
          refereeTypeId: person.refereeTypeId,
        };
        return obj;
      });
    }
  }
  return [];
};

const EventPanelInput: React.FC = () => {
  const intl = useIntl();
  const dispatch = useAppDispatch();

  const comment = useSelector(selectComment);
  const activeMatchEventTypeId = useSelector(selectActiveEventTypeId);
  const activeFocusItem = useSelector(selectActiveFocusItem);
  const activeMatchEvents = useSelector(selectActiveMatchEvents);
  const focusComment = activeFocusItem === ActiveFocusItemName.COMMENT;
  const match = useSelector(selectMatch);

  let textAreaAutoCompleteRef;

  let matchEvent;
  if (activeMatchEvents && !lodash.isEmpty(activeMatchEvents)) {
    matchEvent = getActiveMatchEvent(activeMatchEvents, true);
    if (!matchEvent) {
      const matchEvents = activeMatchEvents.filter(
        (matchEvent) =>
          matchEvent.matchEventTypeId === MatchEventType.PENALTY_MISS ||
          matchEvent.matchEventTypeId === MatchEventType.PENALTIES_MISS,
      );
      if (matchEvents) {
        matchEvent = matchEvents[0];
      }
    }
  }

  const placeholder =
    activeMatchEventTypeId === MatchEventType.INJURY_TIME
      ? intl.formatMessage({ id: 'placeholders.comment.extraTime' })
      : intl.formatMessage({ id: 'placeholders.comment' });

  let disableSubmit = !!(
    activeMatchEventTypeId &&
    [MatchEventType.COMMENT, MatchEventType.INJURY_TIME, MatchEventType.MATCH_SUMMARY].includes(
      activeMatchEventTypeId,
    ) &&
    (!comment || (comment && typeof comment === 'string' && comment.trim() === ''))
  );

  const newEvent = () => {
    dispatch(matchSliceActions.clearActiveMatchEvents());
    dispatch(inputSliceActions.clearEventTime());
    dispatch(inputSliceActions.newEvent());
    dispatch(keyboardActions.setActiveFocusItem(ActiveFocusItemName.COMMENT));
  };

  const onBlur = () => {
    dispatch(keyboardActions.setActiveFocusItem(null));
  };

  const publishEvent = () => {
    dispatch(publishOrUpdateEvent({}));
  };

  const changeComment = (event: any) => {
    let comment = event.target.value;
    if (activeMatchEventTypeId === MatchEventType.INJURY_TIME) {
      comment = comment.replace(/[^0-9]/g, '');
    }
    dispatch(inputSliceActions.setComment(comment));
    if (
      event.target.value.trim() === '' &&
      activeMatchEventTypeId &&
      [MatchEventType.COMMENT, MatchEventType.INJURY_TIME].indexOf(activeMatchEventTypeId) > -1
    ) {
      disableSubmit = true;
    }
  };

  useEffect(() => {
    if (focusComment) {
      textAreaAutoCompleteRef.textareaRef.focus();
    }
  });

  return (
    <>
      <StyledTextArea>
        <ReactTextareaAutocomplete
          className="my-textarea"
          loadingComponent={() => <span>Loading</span>}
          trigger={{
            ':': {
              dataProvider: (token) => dataProviderName(token, match),
              component: Item,
              output: (item) => item.name,
            },
            $: {
              dataProvider: (token) => dataProviderOtherMatchFacts(token, match),
              component: Item,
              output: (item) => item.name,
            },
            '#': {
              dataProvider: (token) => dataProviderShirtNumber(token, match),
              component: Item,
              output: (item) => item.name,
            },
          }}
          ref={(ref) => {
            textAreaAutoCompleteRef = ref;
          }}
          placeholder={placeholder}
          value={comment ?? ''}
          onChange={changeComment}
          onBlur={onBlur}
          listStyle={{
            fontSize: '12px',
            lineHeight: '22px',
            padding: 5,
            listStyleType: 'none',
            backgroundColor: 'white',
            width: '200px',
            border: '1px solid #dfe2e5',
            borderRadius: '3px',
            boxShadow: '0 0 5px rgb(27 31 35 / 10%)',
          }}
          itemStyle={{
            borderBottom: '0.25px solid #dfe2e5',
            paddingLeft: '4px',
          }}
        />
      </StyledTextArea>
      <div className="actions">
        <SecondaryButton greenText onClick={newEvent}>
          <FormattedMessage id="buttons.newEvent" />
        </SecondaryButton>
        <Button onClick={publishEvent} disabled={disableSubmit}>
          <FormattedMessage id={matchEvent && matchEvent.id ? 'buttons.update' : 'buttons.publish'} />
        </Button>
      </div>
    </>
  );
};

export default EventPanelInput;
